import { useState } from "react";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import { useNavigate } from "react-router-dom";
import { TextField, Button, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import { ShieldCheck, Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import AuthShell, { darkFieldSx, authButtonSx } from "./AuthShell";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login({ onLogin }) {
    const [loader, setLoader] = useState(false);
    const [adminId, setAdminId] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    const validate = () => {
        const e = {};
        if (!adminId.trim()) e.adminId = "Admin ID is required";
        else if (!EMAIL_RE.test(adminId.trim())) e.adminId = "Enter a valid email address";
        if (!password) e.password = "Password is required";
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const v = validate();
        if (Object.keys(v).length) {
            setErrors(v);
            return;
        }
        setErrors({});
        try {
            setLoader(true);
            const body = {
                a_email: adminId,
                a_password: password
            };
            const resp = await Calling.AdminLogin(body);
            const { status, token, message } = resp;
            if (status) {
                toast.success(message, {
                    autoClose: 2000,
                });
                onLogin(true);

                localStorage.setItem('admin_trucaller', token);
                navigate("/", { replace: true }); // Replace with the actual route
            } else {
                toast.error(message, {
                    autoClose: 2000,
                });
                setLoader(false);
            }
        } catch (error) {
            console.log(error);
            setLoader(false);
        }

    };

    return (
        <>
            <title>Admin Login</title>

            <AuthShell
                icon={<ShieldCheck size={30} />}
                title="Admin Login"
                subtitle="Sign in to your CallerName admin panel"
            >
                <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                    <TextField
                        label="Admin ID"
                        type="email"
                        value={adminId}
                        onChange={(e) => { setAdminId(e.target.value); if (errors.adminId) setErrors((p) => ({ ...p, adminId: "" })); }}
                        required
                        fullWidth
                        error={!!errors.adminId}
                        helperText={errors.adminId}
                        sx={darkFieldSx}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Mail size={18} color="rgba(255,255,255,0.55)" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        label="Password"
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: "" })); }}
                        required
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password}
                        sx={darkFieldSx}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock size={18} color="rgba(255,255,255,0.55)" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPw((s) => !s)}
                                            edge="end"
                                            aria-label="toggle password visibility"
                                            sx={{ color: "rgba(255,255,255,0.8)", '&:hover': { color: '#34C759' } }}
                                        >
                                            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disableElevation
                        disabled={loader}
                        sx={authButtonSx}
                        startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <LogIn size={18} />}
                    >
                        {loader ? "Signing in..." : "Login"}
                    </Button>
                </form>
            </AuthShell>
        </>
    );
}
